import React, { useState, Component } from 'react';
/**
 * GovernanceRulesTab — Main governance rules browser.
 *
 * Three views: validation rules, rulebook files, and policy thresholds.
 * Canonical location: portals/admin/content/governance/ (DDD — UI in portal)
 */
import { SearchIcon, ShieldCheckIcon, FilterIcon, FolderIcon, CodeIcon, SlidersIcon, SparklesIcon, TagIcon } from 'lucide-react';
import type { Severity, Stage, ViewId } from './governanceRulesData';
import { RULES, PIPELINE_FILES, STAGE_LABELS, STAGE_COLORS, ALL_STAGES } from './governanceRulesData';
import { RuleCard, ThresholdSection } from './GovernanceRulesCards';
import { LiveRulebookCard } from './LiveRulebookCard';
import { RulebookHintsPanel } from './RulebookHintsPanel';
import { listModuleRulebooks, countRoadmapEntries } from '../../../../governance';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
// ─── Color mapping (shortCode → RULEBOOK_COLOR_MAP key) ───────
// Keeps the rulebook cards visually distinct per module. Falls
// back to 'blue' for unknown codes.
const SHORT_CODE_COLOR: Record<string, string> = {
  GET: 'blue',
  REK: 'indigo',
  MET: 'emerald',
  TIJD: 'amber',
  GELD: 'yellow',
  SPR: 'pink',
  SWW: 'rose',
  TAA: 'purple',
  STU: 'cyan',
  TOPO: 'orange'
};
function moduleColorForShortCode(code: string): string {
  return SHORT_CODE_COLOR[code] ?? 'blue';
}
// ─── View Definitions ─────────────────────────────────────────
const VIEWS: {
  id: ViewId;
  label: string;
  icon: React.ReactNode;
}[] = [{
  id: 'regels',
  label: 'Validatieregels',
  icon: <ShieldCheckIcon className="h-4 w-4" data-id="element-4236" />
}, {
  id: 'regelboeken',
  label: 'Regelboeken',
  icon: <FolderIcon className="h-4 w-4" data-id="element-4237" />
}, {
  id: 'family-hints',
  label: 'Family-hints',
  icon: <TagIcon className="h-4 w-4" data-id="element-4238" />
}, {
  id: 'drempelwaarden',
  label: 'Drempelwaarden',
  icon: <SlidersIcon className="h-4 w-4" data-id="element-4239" />
}];
// ─── Severity Counts (static, 5-level) ────────────────────────
// Phase 1: rules can be tagged CRITICAL / REVIEW alongside legacy
// BLOCKER / WARNING / INFO. All five buckets are surfaced so admins
// can filter on semantische ernst (student-facing risk, uncertainty, etc.).
const SEVERITY_TILES: Array<{
  key: Severity;
  label: string;
  textClass: string;
  bgClass: string;
  borderClass: string;
}> = [{
  key: 'CRITICAL',
  label: 'Kritiek',
  textClass: 'text-red-700',
  bgClass: 'bg-red-50',
  borderClass: 'border-red-100'
}, {
  key: 'BLOCKER',
  label: 'Blokkerend',
  textClass: 'text-orange-700',
  bgClass: 'bg-orange-50',
  borderClass: 'border-orange-100'
}, {
  key: 'REVIEW',
  label: 'Review',
  textClass: 'text-yellow-700',
  bgClass: 'bg-yellow-50',
  borderClass: 'border-yellow-100'
}, {
  key: 'WARNING',
  label: 'Coach',
  textClass: 'text-blue-700',
  bgClass: 'bg-blue-50',
  borderClass: 'border-blue-100'
}, {
  key: 'INFO',
  label: 'Info',
  textClass: 'text-slate-700',
  bgClass: 'bg-slate-50',
  borderClass: 'border-slate-100'
}];
const SEVERITY_COUNTS: Record<Severity, number> = {
  CRITICAL: RULES.filter(r => r.severity === 'CRITICAL').length,
  BLOCKER: RULES.filter(r => r.severity === 'BLOCKER').length,
  REVIEW: RULES.filter(r => r.severity === 'REVIEW').length,
  WARNING: RULES.filter(r => r.severity === 'WARNING').length,
  INFO: RULES.filter(r => r.severity === 'INFO').length
};
// ─── Component ────────────────────────────────────────────────
export function GovernanceRulesTab() {
  const [view, setView] = useState<ViewId>('regels');
  const [search, setSearch] = useState('');
  const [selectedStage, setSelectedStage] = useState<Stage | 'all'>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<Severity | 'all'>('all');
  const filtered = RULES.filter(rule => {
    const matchesSearch = search === '' || rule.label.toLowerCase().includes(search.toLowerCase()) || rule.description.toLowerCase().includes(search.toLowerCase()) || rule.id.toLowerCase().includes(search.toLowerCase());
    const matchesStage = selectedStage === 'all' || rule.stage === selectedStage;
    const matchesSeverity = selectedSeverity === 'all' || rule.severity === selectedSeverity;
    return matchesSearch && matchesStage && matchesSeverity;
  });
  return <div className="space-y-5" data-id="element-4240">
      {/* View Switcher */}
      <div className="flex gap-1 rounded-xl border border-gray-100 bg-white p-1" data-id="element-4241">
        {VIEWS.map(v => <button key={v.id} onClick={() => setView(v.id)} className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${view === v.id ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-4242">
            {v.icon}
            <span className="hidden sm:inline" data-id="element-4243">{v.label}</span>
          </button>)}
      </div>

      {/* ── View: Validatieregels ── */}
      {view === 'regels' && <>
          {/* Summary — 5-bucket severity tiles (Phase 1).
                        Click to filter the rule list by that severity. */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-id="element-4244">
            {SEVERITY_TILES.map(tile => {
          const isActive = selectedSeverity === tile.key;
          const count = SEVERITY_COUNTS[tile.key];
          return <button key={tile.key} type="button" onClick={() => setSelectedSeverity(isActive ? 'all' : tile.key)} aria-pressed={isActive} className={`rounded-lg border p-3 text-center transition-all ${tile.bgClass} ${tile.borderClass} ${isActive ? 'ring-2 ring-offset-1 ring-gray-400 shadow-sm' : 'hover:shadow-sm'}`} data-id="element-4245">
                  <p className={`text-2xl font-bold ${tile.textClass}`} data-id="element-4246">
                    {count}
                  </p>
                  <p className={`text-xs font-medium ${tile.textClass}`} data-id="element-4247">
                    {tile.label}
                  </p>
                </button>;
        })}
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 sm:flex-row" data-id="element-4248">
            <div className="flex-1" data-id="element-4249">
              <Input type="text" placeholder="Zoek regels op naam, beschrijving of ID..." value={search} onChange={e => setSearch(e.target.value)} icon={<SearchIcon className="w-4 h-4" data-id="element-4251" />} variant="admin" size="sm" data-id="element-4250" />
            </div>
            <Select value={selectedSeverity} onChange={e => setSelectedSeverity(e.target.value as Severity | 'all')} options={[{
          value: 'all',
          label: 'Alle ernst'
        }, {
          value: 'CRITICAL',
          label: '🔴 Kritiek'
        }, {
          value: 'BLOCKER',
          label: '🟠 Blokkerend'
        }, {
          value: 'REVIEW',
          label: '🟡 Review'
        }, {
          value: 'WARNING',
          label: '🔵 Coach'
        }, {
          value: 'INFO',
          label: '⚪ Info'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-4252" />
          </div>

          {/* Stage Filter Pills */}
          <div className="flex flex-wrap gap-2" data-id="element-4253">
            <FilterIcon className="h-4 w-4 text-gray-400 self-center" data-id="element-4254" />
            {ALL_STAGES.map(stage => <button key={stage} onClick={() => setSelectedStage(stage)} className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${selectedStage === stage ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4255">
                {stage === 'all' ? 'Alle fasen' : STAGE_LABELS[stage as Stage]}
              </button>)}
          </div>

          <p className="text-sm text-gray-500" data-id="element-4256">
            {filtered.length} van {RULES.length} regels weergegeven
          </p>

          <div className="space-y-2" data-id="element-4257">
            {filtered.length === 0 ? <div className="rounded-lg border border-gray-100 bg-gray-50 py-12 text-center" data-id="element-4258">
                <SearchIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" data-id="element-4259" />
                <p className="text-gray-500" data-id="element-4260">
                  Geen regels gevonden voor deze zoekopdracht.
                </p>
              </div> : filtered.map(rule => <RuleCard key={rule.id} rule={rule} data-id="element-4261" />)}
          </div>
        </>}

      {/* ── View: Regelboeken ── */}
      {view === 'regelboeken' && <div className="space-y-5" data-id="element-4262">
          <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4" data-id="element-4263">
            <p className="text-sm text-emerald-800" data-id="element-4264">
              <strong data-id="element-4265">Live regelboeken</strong> — per module een executable
              didactisch profiel uit{' '}
              <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4266">
                src/governance/rulebooks/
              </code>
              . Wat je hier ziet is wat de pipeline daadwerkelijk afdwingt
              (notatie, voorkeur-visuals, vraagtypes, trap-distractors).
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-id="element-4267">
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 border border-violet-100 px-2 py-0.5 font-medium" data-id="element-4268">
                <SparklesIcon className="h-3 w-3" data-id="element-4269" />
                {countRoadmapEntries().futureQuestionTypes} toekomstige
                vraagtypes
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 border border-violet-100 px-2 py-0.5 font-medium" data-id="element-4270">
                <SparklesIcon className="h-3 w-3" data-id="element-4271" />
                {countRoadmapEntries().futureVisuals} toekomstige visuals
              </span>
              <span className="text-emerald-700/70" data-id="element-4272">
                op de roadmap — open een regelboek om de plannen per module te
                zien.
              </span>
            </div>
          </div>

          {/* Live module rulebooks (Phase 3) */}
          <div data-id="element-4273">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2" data-id="element-4274">
              <FolderIcon className="h-4 w-4" data-id="element-4275" />
              Module-Regelboeken ({listModuleRulebooks().length})
            </h3>
            <div className="space-y-2" data-id="element-4276">
              {listModuleRulebooks().map(rb => <LiveRulebookCard key={rb.moduleId} rulebook={rb} color={moduleColorForShortCode(rb.shortCode)} data-id="element-4277" />)}
            </div>
          </div>

          {/* Pipeline source files */}
          <div data-id="element-4278">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2" data-id="element-4279">
              <CodeIcon className="h-4 w-4" data-id="element-4280" />
              Pipeline Bronbestanden (TypeScript)
            </h3>
            <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-4281">
              {PIPELINE_FILES.map((file, i) => <div key={file.path} className={`flex items-start gap-3 px-4 py-3 ${i < PIPELINE_FILES.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-4282">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0 mt-0.5 ${STAGE_COLORS[file.stage]}`} data-id="element-4283">
                    {STAGE_LABELS[file.stage]}
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-4284">
                    <p className="text-sm font-semibold text-gray-800" data-id="element-4285">
                      {file.name}
                    </p>
                    <code className="text-xs text-gray-400 font-mono" data-id="element-4286">
                      {file.path}
                    </code>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-4287">
                      {file.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>}

      {/* ── View: Family-hints (P1-4) ── */}
      {view === 'family-hints' && <RulebookHintsPanel data-id="element-4288" />}

      {/* ── View: Drempelwaarden ── */}
      {view === 'drempelwaarden' && <ThresholdSection data-id="element-4289" />}
    </div>;
}